import axios from 'axios';

import { triggerBlobDownload } from '@/utils/blobDownload';

/**
 * Minimal client for the V3 (event-sourced / Temporal) reporting workflow:
 * `POST /v2/orgs/{orgId}/report-runs` → poll `.../status` → `.../download`.
 *
 * Kept as plain functions (not a generated-client wrapper) because the checked-in
 * generated api-svc client predates the newer V3 report types, and
 * `generated/*` must never be hand-edited. Same approach as the V3 paths in
 * web-app-v3's BalanceReportExpanded.
 */

export interface ReportRunInput {
  key: string;
  value: string;
}

export type ReportRunPollOutcome = 'succeeded' | 'failed' | 'timed-out' | 'aborted';

export interface ReportRunPollResult {
  outcome: ReportRunPollOutcome;
  /**
   * For a `failed` outcome: the diagnostic(s) api-svc recorded on the run
   * (e.g. the report definition's refusal message), when it sent any.
   */
  errors?: string[];
}

/** Submits a V3 report run; resolves to the new run's id. */
export async function submitReportRun(
  baseUrl: string,
  orgId: string,
  reportType: string,
  inputs: ReportRunInput[]
): Promise<string> {
  const resp = await axios.post(
    `${baseUrl}v2/orgs/${orgId}/report-runs`,
    { reportType, inputs },
    { withCredentials: true }
  );
  const runId = resp.data?.reportRunId;
  if (!runId) {
    throw new Error('Report run submission did not return a run id');
  }
  return runId;
}

export interface PollOptions {
  intervalMs: number;
  /**
   * Client-side cap on waiting. The V3 pipeline can leave a run non-terminal
   * for up to its 60m workflow timeout on some infra failures — the UI must
   * not spin that long, so give up and report 'timed-out' at this deadline.
   */
  timeoutMs: number;
  /** Return true to stop polling early (e.g. component destroyed). */
  isAborted?: () => boolean;
}

/**
 * Polls a run's status until it is terminal, aborted, or past the deadline,
 * returning the outcome together with the errors api-svc recorded on a
 * failed run (its `/status` response carries them as `errors`).
 *
 * Transient status-request failures (network errors, 5xx — e.g. LB blips
 * during sidecar rolls) must not fail a run that is still progressing
 * server-side, so they are swallowed and polling continues; the deadline
 * bounds how long that can go on. Deterministic 4xx responses (bad run id,
 * revoked auth) will never heal, so those still reject.
 */
export async function pollReportRun(
  baseUrl: string,
  orgId: string,
  runId: string,
  opts: PollOptions
): Promise<ReportRunPollResult> {
  const deadline = Date.now() + opts.timeoutMs;
  for (;;) {
    let status: string | undefined;
    let errors: string[] | undefined;
    try {
      const resp = await axios.get(`${baseUrl}v2/orgs/${orgId}/report-runs/${runId}/status`, {
        withCredentials: true,
      });
      status = resp.data?.status;
      errors = reportRunErrors(resp.data?.errors);
    } catch (err) {
      const httpStatus = (err as { response?: { status?: number } })?.response?.status;
      if (httpStatus !== undefined && httpStatus >= 400 && httpStatus < 500) {
        throw err;
      }
    }
    if (status === 'succeeded' || status === 'timed-out') {
      return { outcome: status };
    }
    if (status === 'failed') {
      return errors === undefined ? { outcome: status } : { outcome: status, errors };
    }
    if (opts.isAborted?.()) {
      return { outcome: 'aborted' };
    }
    if (Date.now() >= deadline) {
      return { outcome: 'timed-out' };
    }
    await new Promise((resolve) => setTimeout(resolve, opts.intervalMs));
  }
}

/** Polls a run's status until it is terminal, aborted, or past the deadline; outcome only. */
export async function pollReportRunStatus(
  baseUrl: string,
  orgId: string,
  runId: string,
  opts: PollOptions
): Promise<ReportRunPollOutcome> {
  return (await pollReportRun(baseUrl, orgId, runId, opts)).outcome;
}

/** The non-empty strings of a `/status` response's `errors`, or undefined when it carried none. */
function reportRunErrors(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const errors = raw.filter((e): e is string => typeof e === 'string' && e.trim().length > 0);
  return errors.length > 0 ? errors : undefined;
}

/**
 * Extracts the server-chosen filename from a Content-Disposition header.
 * Cross-origin the header is only readable when the API exposes it via
 * Access-Control-Expose-Headers, so callers must pass a fallback.
 */
export function csvFilenameFromHeaders(headers: Record<string, unknown>, fallback: string): string {
  const disposition = headers?.['content-disposition'];
  if (typeof disposition !== 'string') {
    return fallback;
  }
  const match = /filename="([^"]+)"/.exec(disposition);
  return match ? match[1] : fallback;
}

/** Fetches a succeeded run's CSV and triggers a browser download for it. */
export async function downloadReportRunCsv(
  baseUrl: string,
  orgId: string,
  runId: string,
  fallbackFilename: string
): Promise<void> {
  const resp = await axios.get(`${baseUrl}v2/orgs/${orgId}/report-runs/${runId}/download`, {
    withCredentials: true,
    responseType: 'blob',
  });
  // `triggerBlobDownload` defers the revoke: revoking in the same tick as the
  // click aborts large downloads in Chromium, leaving a 0-byte file.
  triggerBlobDownload(
    new Blob([resp.data], { type: 'text/csv' }),
    csvFilenameFromHeaders(resp.headers, fallbackFilename)
  );
}
